/* eslint-disable @typescript-eslint/no-empty-function */
import { useState, useEffect, createContext } from 'react';

import { useContextSelector } from 'use-context-selector';

import { amplitudeInstance } from '../config/amplitude';
import buildRouteUrl from '../helpers/buildRouteUrl';
import { setLanguage } from '../helpers/language';
import { getUserToken, logout } from '../helpers/session';
import api from '../services/api';
import {
  ProfileImage,
  ProfileVerification,
  UserSubscription
} from '../types/types';
import AppContext from './AppContext';

interface UserData {
  _id?: string;
  name?: string;
  username?: string;
  profile_image: ProfileImage;
  email?: string;
  content_region?: string;
  selected_language?: string;
  gender?: string;
  birthdate?: string;
  email_verified_at?: string;
  verifications?: ProfileVerification[];
}

interface AuthContextI {
  isLoading: boolean;
  isLoadingSubscription: boolean;
  isLoggedIn: boolean;
  token: string | null;
  userData: UserData | null;
  userSubscription: UserSubscription | null;
  setData(data: UserData): void;
  setUserSubscription(data: UserSubscription): void;
  destroySession(): void;
  deleteSession(): void;
}

interface PropTypes {
  value?: AuthContextI;
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextI>({} as any);

export default AuthContext;

export const AuthProvider: React.FC<PropTypes> = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingSubscription, setIsLoadingSubscription] =
    useState<boolean>(true);
  const [token, setToken] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [userSubscription, setUserSubscription] =
    useState<UserSubscription | null>(null);

  const refetchClientConfig = useContextSelector(
    AppContext,
    app => app.refetchClientConfig
  );

  async function getUserData() {
    try {
      const response = await api.get('/v1/me');

      setUserData({
        _id: response.data.data._id,
        name: response.data.data.name,
        username: response.data.data.username,
        email: response.data.data.email,
        profile_image: response.data.data.profile_image,
        gender: response.data.data.gender,
        birthdate: response.data.data.birthdate,
        content_region: response.data.data.content_region,
        selected_language: response.data.data.selected_language,
        email_verified_at: response.data.data.email_verified_at
      });
    } catch (err: any) {
      if (err?.response?.status === 401) {
        logout();

        window.location = '/' as unknown as Location;
      }
    }
  }

  async function getUserSubscription() {
    try {
      setIsLoadingSubscription(true);
      const response = await api.get('/v1/me/subscription');

      setUserSubscription(response.data.data);
      setIsLoadingSubscription(false);
    } catch (err: any) {
      setIsLoadingSubscription(false);
      if (err?.response?.status === 404) {
        setUserSubscription(null);
      }
    }
  }

  useEffect(() => {
    async function getToken() {
      setIsLoading(true);

      const userSession = getUserToken();

      if (userSession.isLoggedIn) {
        setToken(userSession.token);
        await getUserData();

        refetchClientConfig();

        await getUserSubscription();
      }

      if (!userSession.isLoggedIn) {
        refetchClientConfig();

        setToken(null);
        setUserData(null);
      }

      setIsLoading(false);
    }

    getToken();
  }, []);

  useEffect(() => {
    if (userData?.selected_language) {
      setLanguage(userData.selected_language);
    }
  }, [userData]);

  function setData(data: UserData) {
    setUserData(data);
  }

  async function destroySession() {
    await api.delete('/v1/logout');

    amplitudeInstance.logEvent('Logout');
    amplitudeInstance.setUserId(null);
    amplitudeInstance.regenerateDeviceId();

    logout();

    window.location = buildRouteUrl('/') as unknown as Location;
  }

  async function deleteSession() {
    amplitudeInstance.setUserId(null);
    amplitudeInstance.regenerateDeviceId();

    logout();
  }

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isLoadingSubscription,
        isLoggedIn: !!token,
        token,
        userData,
        setData,
        setUserSubscription,
        userSubscription,
        destroySession,
        deleteSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
